import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { api } from '../services/api';
import { useReadOnly } from '../hooks/useReadOnly';

/**
 * The job scope on a bid, written by Claude from the bid's line items
 * (server/services/bidScope). Draft → read and edit → Use this. The agreement
 * the customer signs is rebuilt from it; every price in it still comes from
 * the lines, never from the AI. Mowing and lawn-plan bids keep the owner's own
 * agreement templates and do not show this.
 */
type WorkItem = { line: string; optional?: boolean; steps: string[] };
type Scope = {
  headline: string; work: WorkItem[]; materials: string[]; not_included: string[];
  customer_prep: string[]; office_checks: string[];
};

const toLines = (xs?: string[]) => (xs || []).join('\n');
const fromLines = (t: string) => t.split('\n').map(x => x.replace(/^\s*[-•]\s*/, '').trim()).filter(Boolean);

export default function BidScopeEditor({ quote, onSaved }: { quote: any; onSaved: () => void }) {
  const readOnly = useReadOnly();
  const [busy, setBusy] = useState<'draft' | 'save' | 'clear' | null>(null);
  const [draft, setDraft] = useState<Scope | null>(null);
  const [preview, setPreview] = useState<string>('');
  const [showPreview, setShowPreview] = useState(false);
  const saved: Scope | null = quote?.ai_scope_json || null;
  const locked = !!quote?.agreement_signed_at || !['draft', 'sent'].includes(String(quote?.status || 'draft'));

  if (!quote?.ai_scope_available) return null;

  async function write() {
    setBusy('draft');
    try {
      const { data } = await api.post('/ai/bid-scope', { quote_id: quote.id });
      setDraft(data.scope);
      setPreview(data.agreement_preview || '');
      setShowPreview(false);
      if ((data.scope?.office_checks || []).length) toast.info('Drafted — it has questions for you before sending');
      else toast.success('Drafted — read it, edit anything, then Use this');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not write the scope');
    } finally { setBusy(null); }
  }

  async function apply() {
    if (!draft) return;
    setBusy('save');
    try {
      await api.post('/ai/bid-scope/apply', { quote_id: quote.id, scope: draft });
      setDraft(null);
      toast.success('Scope and agreement saved on the bid');
      onSaved();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not save the scope');
    } finally { setBusy(null); }
  }

  async function clear() {
    if (!window.confirm('Remove the written scope and go back to the standard agreement for this kind of job?')) return;
    setBusy('clear');
    try {
      await api.delete(`/ai/bid-scope/${quote.id}`);
      toast.success('Back to the standard agreement');
      onSaved();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not remove it');
    } finally { setBusy(null); }
  }

  const set = (patch: Partial<Scope>) => setDraft(d => (d ? { ...d, ...patch } : d));
  const setSteps = (i: number, text: string) => setDraft(d => (d ? { ...d, work: d.work.map((w, j) => (j === i ? { ...w, steps: fromLines(text) } : w)) } : d));

  return (
    <div className="mb-4 rounded-lg border border-purple-200 bg-purple-50/40 p-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Job scope</p>
        {!locked && !draft && (
          <div className="flex items-center gap-3">
            <button onClick={write} disabled={!!busy || readOnly}
              className="text-xs text-purple-700 hover:text-purple-900 font-semibold disabled:opacity-40">
              {busy === 'draft' ? 'Reading the lines…' : saved ? '✨ Rewrite from the lines' : '✨ Write from the lines'}
            </button>
            {saved && (
              <button onClick={clear} disabled={!!busy || readOnly} className="text-xs text-gray-500 hover:text-red-700 disabled:opacity-40">Remove</button>
            )}
          </div>
        )}
      </div>

      {!draft && !saved && (
        <p className="text-xs text-gray-500">AI reads the line items and writes what the crew will actually do, what isn't included and what the customer should do first. You read it before anything is saved; the agreement is built from it with your usual terms, and the prices always come from the lines.</p>
      )}

      {!draft && saved && (
        <div className="text-xs text-gray-700 space-y-1.5">
          {quote.ai_scope_stale && (
            <p className="font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">The line items changed since this was written — rewrite it so the scope matches the bid.</p>
          )}
          {saved.headline && <p>{saved.headline}</p>}
          <ul className="list-disc pl-4 space-y-0.5">
            {saved.work.map((w, i) => <li key={i}><span className="font-semibold">{w.line}</span>{w.steps.length ? ` — ${w.steps.join('; ')}` : ''}</li>)}
          </ul>
          {saved.office_checks?.length > 0 && (
            <div className="text-amber-900"><span className="font-semibold">Your questions:</span> {saved.office_checks.join(' · ')}</div>
          )}
        </div>
      )}

      {draft && (
        <div className="space-y-2">
          {draft.office_checks.length > 0 && (
            <div className="rounded border border-amber-300 bg-amber-50 px-2.5 py-2">
              <p className="text-xs font-semibold text-amber-900 mb-1">Check before sending (only you see these)</p>
              <ul className="list-disc pl-4 text-xs text-amber-900 space-y-0.5">{draft.office_checks.map((c, i) => <li key={i}>{c}</li>)}</ul>
            </div>
          )}
          <label className="block">
            <span className="text-[11px] font-semibold text-gray-600">What the job is (the customer reads this first)</span>
            <textarea value={draft.headline} onChange={e => set({ headline: e.target.value })} rows={2} className="input text-xs w-full" />
          </label>
          {draft.work.map((w, i) => (
            <label key={i} className="block">
              <span className="text-[11px] font-semibold text-gray-600">{w.line}{w.optional ? ' (optional)' : ''} — one step per line</span>
              <textarea value={toLines(w.steps)} onChange={e => setSteps(i, e.target.value)} rows={Math.min(6, Math.max(2, w.steps.length))} className="input text-xs w-full" />
            </label>
          ))}
          {([['materials', 'Materials we supply'], ['not_included', 'Not included'], ['customer_prep', 'Before we come']] as const).map(([k, label]) => (
            <label key={k} className="block">
              <span className="text-[11px] font-semibold text-gray-600">{label} — one per line</span>
              <textarea value={toLines(draft[k])} onChange={e => set({ [k]: fromLines(e.target.value) } as Partial<Scope>)} rows={Math.min(5, Math.max(2, draft[k].length))} className="input text-xs w-full" />
            </label>
          ))}
          {preview && (
            <div>
              <button onClick={() => setShowPreview(v => !v)} className="text-xs text-brand-600 font-medium">{showPreview ? 'Hide' : 'Show'} the agreement as the customer would sign it</button>
              {showPreview && <pre className="mt-1 text-[11px] text-gray-600 whitespace-pre-wrap font-sans bg-white border border-gray-200 rounded p-2 max-h-64 overflow-y-auto">{preview}</pre>}
            </div>
          )}
          <div className="flex gap-2 pt-1">
            <button onClick={apply} disabled={!!busy || readOnly} className="btn-primary text-xs py-1 px-3 disabled:opacity-50">{busy === 'save' ? 'Saving…' : 'Use this'}</button>
            <button onClick={() => setDraft(null)} disabled={!!busy} className="btn-secondary text-xs py-1 px-3">Discard</button>
          </div>
          <p className="text-[11px] text-gray-500">Edits here are what the customer sees. Prices are never taken from this — they print from the line items.</p>
        </div>
      )}
    </div>
  );
}
